import { useEffect, useState } from "react";
import { post } from "@common/api/apiShared.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { SystemTemplateTypesEnum } from "../systemEdit/SystemEdit.types";
import { AddSystemPaneProps } from "./AddSystemPane.types";
import {
    CreateSystemSaveModel,
    createSystemSaveModel,
    getSystemFieldValue,
    mapEditedSystemToSaveFields,
    SystemCreationFieldSelections,
    systemCreationFieldIds,
    toCreateSystemRequest,
} from "../systemCreation.ts";
import { useValidator } from "@common/hooks/validation";
import { GetSystemsPlanLimit } from "../../auth/planProvider/PlanProvider.hooks.ts";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useDismissable } from "@common/hooks/useDismissable";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries.ts";
import {
    EntityTemplate,
    EntityTemplateField,
    getStandardFieldTranslationKey,
    SystemStandardField,
    TemplateEntityType,
} from "../../entityTemplates/EntityTemplate.types.ts";
import { hasFieldData } from "../../entityTemplates/fieldValue.ts";
import { SystemModel } from "../systemEdit/SystemEdit.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { groupFieldsByTemplateStep } from "../../entityTemplates/templateStepFields/groupFieldsByTemplateStep.ts";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { buildSystemDefaults, fieldsTheUserMayEdit } from "../../entityTemplates/defaultValue/templateDefaults.ts";

export function useAddSystemPane(props: AddSystemPaneProps) {
    const { translateString } = useTranslation();
    const { refetch: refetchSystemsPlanLimitData } = GetSystemsPlanLimit();
    const { informationSecurityFeatures, platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { close, setHasUnsavedChanges } = usePane();

    const { visible: showInfoSecUpgradeAlert, onDismiss: onDismissInfoSecAlert } = useDismissable(
        "info-sec-upgrade-alert-dismissed",
        30,
        !informationSecurityFeatures.hasInfoSec
    );

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.System, false);

    const templates: Array<DotLegalSelectOption> | undefined = entityTemplatesData?.map((template) => ({
        id: template.id,
        name: template.name,
    }));

    const [systemModel, setSystemModel] = useState<CreateSystemSaveModel>(createSystemSaveModel(props.searchedName ?? ""));
    // Empty until the user changes something, so the template's default values show through until then
    const [systemEdits, setSystemEdits] = useState<Partial<SystemModel>>({});
    const [editedTagIds, setEditedTagIds] = useState<Array<string> | undefined>(undefined);
    const [creationFieldEdits, setCreationFieldEdits] = useState<SystemCreationFieldSelections>({});
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    const templateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${systemModel.entityTemplateId}`, { enabled: !!systemModel.entityTemplateId });
    const template = templateQuery.data;

    const templateFields = template?.steps.flatMap((step) => step.fields) ?? [];
    const templateDefaults = buildSystemDefaults(fieldsTheUserMayEdit(templateFields, platformFeatures.tags && permissions.canAccessTags));
    const editedSystem: Partial<SystemModel> = { ...templateDefaults.system, ...systemEdits };
    const selectedTagIds = editedTagIds ?? templateDefaults.tagIds;
    const creationFieldSelections: SystemCreationFieldSelections = { ...templateDefaults.creationFieldSelections, ...creationFieldEdits };

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === systemModel.entityTemplateId);
    // The full template carries no such flag: the selectable view model is what reports that the user is
    // barred from a field this template insists on at creation time.
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const tagsAvailable = platformFeatures.tags && permissions.canAccessTags;

    const requiredAtCreationFields = templateFields.filter(
        (field) =>
            field.requiredAtCreation &&
            field.standardFieldIdentifier !== SystemStandardField.SystemStatus &&
            (field.standardFieldIdentifier !== SystemStandardField.SystemTags || tagsAvailable)
    );

    const fieldGroups = groupFieldsByTemplateStep(template, requiredAtCreationFields);

    const validation = useValidator<CreateSystemSaveModel>((validators) => ({
        name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("assetTemplate")),
    }));

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        for (const field of requiredAtCreationFields) {
            // The name is validated with the save model, where the pane keeps it
            if (field.standardFieldIdentifier === SystemStandardField.SystemName) {
                continue;
            }

            if (field.standardFieldIdentifier === SystemStandardField.SystemTags) {
                if (selectedTagIds.length === 0) {
                    errors.push(new ValidationError(getStandardFieldTranslationKey(SystemStandardField.SystemTags), requiredTranslation));
                }
                continue;
            }

            if (field.standardFieldIdentifier && systemCreationFieldIds.has(field.standardFieldIdentifier)) {
                const selectedIds = creationFieldSelections[field.standardFieldIdentifier as SystemStandardField] ?? [];
                if (selectedIds.length === 0) {
                    errors.push(new ValidationError(getStandardFieldTranslationKey(field.standardFieldIdentifier), requiredTranslation));
                }
                continue;
            }

            const fieldName = field.standardFieldIdentifier ? SystemStandardField[field.standardFieldIdentifier].toString() : field.id;
            const value = getSystemFieldValue(field, editedSystem);
            if (!hasFieldData(value)) {
                errors.push(new ValidationError(fieldName, requiredTranslation));
            }
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: any) => {
            const id = response.value().id;

            refetchSystemsPlanLimitData();
            // A pane outlives a navigation, so it closes before the caller moves on to the new asset
            close();
            props.onCreated?.(id);
        },
    });

    let templateTypes: Array<DotLegalSelectOption> = Object.entries(SystemTemplateTypesEnum)
        .filter((q) => isNaN(Number(q[0])))
        .map((q) => {
            return {
                name: translateString(q[0].toLowerCase()),
                id: q[1].toString(),
            };
        });

    const createSystem = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }
        const racErrors = validateRequiredAtCreationFields();
        if (racErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }
        if (validation.anyHasErrors || racErrors.length > 0) {
            return;
        }

        await saveMutation.mutateAsync({
            ...systemModel,
            ...mapEditedSystemToSaveFields(editedSystem, selectedTagIds),
            // The server fills in the defaults of every other field
            answeredFieldIds: requiredAtCreationFields.map((field) => field.id),
            supplierLegalEntityIds: creationFieldSelections[SystemStandardField.SystemSuppliers] ?? [],
            associatedAssetIds: creationFieldSelections[SystemStandardField.SystemAssociatedAssets] ?? [],
            dataSubjectIds: creationFieldSelections[SystemStandardField.SystemDataSubjects] ?? [],
            dataCategoryIds: creationFieldSelections[SystemStandardField.SystemDataCategories] ?? [],
            securityMeasureIds: creationFieldSelections[SystemStandardField.SystemSecurityMeasures] ?? [],
            policyIds: creationFieldSelections[SystemStandardField.SystemPolicies] ?? [],
        });
    };

    function save(model: CreateSystemSaveModel) {
        return post<{}>("/System/", toCreateSystemRequest(model));
    }

    const selectTemplate = (entityTemplateId: string | null) => {
        if (entityTemplateId) {
            setSystemModel((prev) => ({ ...prev, entityTemplateId }));
            setSystemEdits({});
            setEditedTagIds(undefined);
            setCreationFieldEdits({});
            setShowRequiredFieldErrors(false);
        }
    };

    useEffect(() => {
        if (entityTemplatesData?.length === 1 && !systemModel.entityTemplateId) {
            selectTemplate(entityTemplatesData[0].id);
        }
    }, [entityTemplatesData, systemModel.entityTemplateId]);

    const onModelChange = (next: CreateSystemSaveModel) => {
        setHasUnsavedChanges(true);
        setSystemModel(next);
    };

    const onTemplateChange = (entityTemplateId: string | null) => {
        setHasUnsavedChanges(true);
        selectTemplate(entityTemplateId);
    };

    const onTemplateFieldChange = async (changeFunc: (system: SystemModel) => void) => {
        setHasUnsavedChanges(true);
        const updated = { ...editedSystem } as SystemModel;
        changeFunc(updated);
        setSystemEdits(updated);
    };

    const onTagsChange = (tagIds: Array<string>) => {
        setHasUnsavedChanges(true);
        setEditedTagIds(tagIds);
    };

    const onCreationFieldChange = (field: SystemStandardField, selectedIds: Array<string>) => {
        setHasUnsavedChanges(true);
        setCreationFieldEdits((prev) => ({ ...prev, [field]: selectedIds }));
    };

    return {
        inProgress: saveMutation.isLoading,
        createSystem,
        systemModel,
        onModelChange,
        validation,
        templateTypes,
        templates,
        templatesIsLoading,
        isLoadingTemplate: templateQuery.isLoading && !!systemModel.entityTemplateId,
        showInfoSecUpgradeAlert,
        onDismissInfoSecAlert,
        selectedTemplateMissingAccess,
        fieldGroups,
        selectedTagIds,
        onTagsChange,
        creationFieldSelections,
        onCreationFieldChange,
        requiredFieldValidationErrors,
        editedSystem,
        onTemplateFieldChange,
        onTemplateChange,
        permissions,
    };
}
